import { observer } from "mobx-react"
import { Flex, Table, Tag, Typography } from "antd"
import { ColumnsType } from "antd/es/table"
import { choose } from "choose-matching"

import { LoadingBlock } from "@src/app/components/LoadingBlock"
import { NoContent } from "@src/app/components/NoContent/Index"
import { IdItem } from "@src/app/components/tableRowItems/idItem"
import { GlobalCurrenciesCollection } from "@src/app/CurrenciesCollection"
import { ChargesCollection } from "@src/pages/transactionsIn/classes/ChargesCollection"
import { TransactionInListExpandable } from "@src/pages/transactionsIn/ui/TransactionInListExpandable"
import { PAYMENTS_PRICING_TYPES_OPTIONS } from "@src/pages/transactionsPayments/constants"
import { ChargeResponse } from "@src/shared/api/generated"
import { WithCollection } from "@src/shared/classes/Collection/types"
import { DateTimeTableCell } from "@src/shared/componemts/DateTimeTableCell"
import { parsePricing, Pricing } from "@src/shared/parsePricing"

export const TransactionInTable = observer(
  ({ collection }: WithCollection<ChargesCollection>) => {
    const columns: ColumnsType<ChargeResponse> = [
      {
        dataIndex: "id",
        render: (_, { id }) => <IdItem id={id!} />,
        title: "ID",
        width: 170,
      },
      {
        dataIndex: "project_id",
        render: (_, { project_id }) => <IdItem id={project_id!} />,
        title: "Project ID",
        width: 170,
      },
      {
        dataIndex: "external_id",
        render: (_, { external_id }) => {
          if (!external_id) {
            return "-"
          }

          return external_id.length > 6 ? (
            <IdItem cut id={external_id} />
          ) : (
            <IdItem id={external_id} p={external_id.length} />
          )
        },
        title: "External ID",
        width: 130,
      },
      {
        dataIndex: "status",
        render: (_, { status }) => (
          <Tag color={status === "completed" ? "green" : "geekblue"}>
            {status}
          </Tag>
        ),
        title: "Status",
        width: 115,
      },
      {
        dataIndex: "pricing_type",
        render: (_, { pricing_type }) => {
          const pricingTypeItem = PAYMENTS_PRICING_TYPES_OPTIONS.find(
            (i) => i.value === (pricing_type as any),
          )

          return pricingTypeItem?.label
        },
        title: "Pricing type",
        width: 120,
      },
      {
        dataIndex: "init_price",
        render: (_, { init_price }) => {
          if (!init_price) {
            return "Donation"
          }

          const currency = GlobalCurrenciesCollection().getBySymbolNetwork({
            network: init_price.network!,
            symbol: init_price.symbol!,
          })

          return (
            <Typography.Text>
              {`${init_price?.amount} ${currency?.symbol_alias ?? init_price?.symbol}`}
            </Typography.Text>
          )
        },
        title: (
          <Flex align="center" vertical>
            <span>Amount</span>
            <span>(Fiat)</span>
          </Flex>
        ),
        width: 100,
      },
      {
        dataIndex: "pricing",
        render: (_, { pricing, ...rest }) => {
          if (!pricing) {
            return "-"
          }

          const cryptoAmount = parsePricing(pricing as Pricing)

          if (!cryptoAmount) {
            return "-"
          }

          const currency = GlobalCurrenciesCollection().getBySymbolNetwork({
            network: cryptoAmount.network,
            symbol: cryptoAmount.symbol,
          })

          return (
            <div>
              {cryptoAmount?.amount_paid
                ? `${cryptoAmount.amount_paid} ${currency?.symbol_alias ?? cryptoAmount.symbol}`
                : "0"}
            </div>
          )
        },
        title: (
          <Flex align="center" vertical>
            <span> Amount</span>
            <span>(Crypto)</span>
          </Flex>
        ),
        width: 110,
      },
      {
        dataIndex: "merchant_price",
        render: (_, { merchant_price }) => {
          if (!merchant_price) {
            return "-"
          }

          const currency = GlobalCurrenciesCollection().getBySymbolNetwork({
            network: merchant_price?.network ?? "",
            symbol: merchant_price?.symbol ?? "",
          })

          return (
            <div>
              {merchant_price
                ? `${merchant_price?.amount_paid} ${currency?.symbol_alias ?? merchant_price?.symbol}`
                : "-"}
            </div>
          )
        },
        title: (
          <Flex align="center" vertical>
            <span>Amount</span>
            <span> (Credited)</span>
          </Flex>
        ),
        width: 130,
      },
      {
        dataIndex: "created_at",
        render: (_, { created_at }) => <DateTimeTableCell value={created_at} />,
        title: "Created at",
        width: 150,
      },
    ]

    return (
      <>
        {choose(
          [collection.isLoading && collection.isEmpty, <LoadingBlock />],
          [
            collection.isEmpty,
            <NoContent
              paragraph="Wait for your first transaction to start receiving reports."
              title="No Transactions Yet"
            />,
          ],
          [
            !collection.isEmpty,
            <Table
              columns={columns}
              dataSource={collection.items}
              expandable={{
                expandedRowRender: (record) => (
                  <TransactionInListExpandable
                    charge={record}
                    isLoading={collection.isLoading}
                  />
                ),
              }}
              loading={collection.isLoading}
              pagination={{
                ...collection.pagination,
              }}
              rowKey="id"
              scroll={{ x: 1240 }}
            />,
          ],
        )}
      </>
    )
  },
)
